import React, { useCallback, useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  ContentBody,
  ContentContainer,
  ContentHeader,
  Links,
  SocialMedia,
  ClipEntry,
  ClipText,
  MetaRow,
  HdBanner,
  HdBadge,
  HdBannerText,
  HdBannerLink,
  ViralSection,
  ViralHeader,
  ViralTitleRow,
  ViralFeatureGrid,
  ViralFeatureCard,
  ViralFeatureCardWrap,
  ViralFeatureBadge,
  ReframeInlineProgress,
  ReframeInlineProgressBar,
  ReframeInlineProgressFill,
  ReframeSection,
  ReframeIcon,
  ReframeContentCol,
  ReframeTextRow,
  PanelOverlay,
} from "./shorts-components";
import Typography from "../../common-components/Typography";
import vmTheme from "../../constants/theme";
import { font } from "../../constants/font";
import content from "../../constants/content";
import Action from "../../common-components/Action";
import CustomTooltip from "../../common-components/CustomTooltip";
import openUrl from "../../helper/openUrl";
import { API_URL, STATIC_PATH, API, SITE_URL, ENVIRONMENT, REFRAME_DOMAIN } from "../../constants/config";
import Export from "../modal/export";
import useApi from "../../helper/hooks/useApi";
import { secondsToMinute } from "../timeline/timeline-helper";
import { Divider, FlexDiv } from "./shorts-list";
import { useShortsContext } from "./shortsContextApi";
import contextActionType from "./context-action-type";
import { PLAN, VIRAL_FEATURES, isShortsFlowSourceType } from "../../constants";
import { playPausePlayer, setPlayAll, setPlayHeadTime, reframeStatusApi, setReframeData } from "../../redux/actions/appUtils";
import { updateProjectData } from "../../redux/actions/timelineUtils";
import ReframeProgressModal from "../../common-components/ReframeProgressModal";
import ShortsFeedbackButtons from "./shorts-feedback-buttons";
import TRACKERS from "../../constants/trackers";
import trackerEvents from "../../helper/trackerEvents";
import { addNotification } from "../../redux/actions/notificationUtils";
import uuid from "../../helper/uuid";
import ErrorModal from "../../common-components/ErrorModal";
import getAwsAccess, { uploadFile } from "../../helper/getAwsAccess";
import { captureShortReframeThumbnail } from "../../helper/captureShortReframeThumbnail";
import { useCustomCallback } from "../../helper/hooks/useCustomCallback";
import { isLiteReframeRestricted } from "../../helper/checkUser";

const { OPEN_SHORT_PRESET, SET_ACTIVE_PLAYER, SET_REFRAME_LOADING, SET_REFRAME_PROGRESS } = contextActionType;

const PRICING_MODAL_ARGS = { type: "longtoshort", source: "free-upgrade-shorts" };



const ShortsContent = ({
  shortData,
  inModal = false,
  showPricingModal,
  feedbackType,
  onLike,
  onDislike,
  parentDispatch,
}) => {
  const dispatch = useDispatch();
  const { post } = useApi();
  const notifyDispatch = parentDispatch || dispatch;
  const success = useCallback((description, subText) => {
    notifyDispatch(addNotification({
      id: uuid(),
      toastType: "SUCCESS",
      description,
      subText,
    }));
  }, [notifyDispatch]);
  const theme = useSelector((state) => state.app.get("theme"));
  const t1 = useSelector((state) => state.app.get("t1"));
  const projectDetails = useSelector((state) => state.projectDetails);
  const { permission, projectId, plan, dimensionName, userId, teamId, currentTeamId, sourceType } = useSelector((state) => state.userDetails);
  const { setShortsContext } = useShortsContext();

  const hasSubtitle = !!projectDetails.get("localSubtitle").size;
  const isReframeRequired = React.useMemo(() => {
    if (!projectDetails) return false;
    const workspaceItems = projectDetails.get
      ? projectDetails.get("workspaceItems")
      : projectDetails.workspaceItems;
    if (!workspaceItems) return false;

    const videoItem = workspaceItems.valueSeq
      ? workspaceItems.valueSeq().find((item) => (item.get ? item.get("type") : item.type) === "UPVIDEO")
      : Object.values(workspaceItems).find((item) => item.type === "UPVIDEO");

    if (!videoItem) return false;
    return videoItem.get
      ? videoItem.get("reframe_required") === true
      : videoItem.reframe_required === true;
  }, [projectDetails]);

  const isReframeShort = shortData?.get
    ? (shortData.get("isReframe") || (shortData.get("clips") && typeof shortData.get("clips").some === "function" && shortData.get("clips").some(c => c.get ? c.get("isReframe") : c.isReframe)))
    : (shortData?.isReframe || shortData?.clips?.some(c => c.isReframe));
  const isReframeRestricted = isLiteReframeRestricted(plan);
  const [isModal, setIsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isReframeLoading, setIsReframeLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, title: "", message: "", onRetry: null });

  const cancelReframeRef = useRef(false);
  const [reframeProgress, setReframeProgress] = useState(0);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [reframeApplied, setReframeApplied] = useState(false);
  const isPanelBlocked = isReframeLoading || showProgressModal;
  const progressIntervalRef = useRef(null);
  // Tracks clip reframeSrc URLs that have already been applied to workspace items
  const processedReframeClipsRef = useRef(new Set());
  const didShortThumbnailRef = useRef(false);
  const scheduleThumbnailCaptureRef = useRef(null);
  const captureShortThumbnailRef = useRef(null);
  const pendingPlaybackResumeRef = useRef(false);
  const prevT1ForResumeRef = useRef(t1);
  const playbackResumeTimersRef = useRef([]);

  const shortId = shortData.get ? shortData.get("id") : shortData?.id;
  const appReframeData = useSelector((state) => state.app.get("reframeData"));

  // Always prefer projectDetails.shorts — shortData prop can lag after reframe API updates
  const resolvedShort = useMemo(() => {
    const shortsMap = projectDetails?.get
      ? projectDetails.get("shorts")
      : projectDetails?.shorts;
    if (shortsMap && shortId) {
      const fromProject = shortsMap.get ? shortsMap.get(shortId) : shortsMap[shortId];
      if (fromProject) {
        return fromProject.toJS ? fromProject.toJS() : { ...fromProject };
      }
    }
    return shortData?.toJS ? shortData.toJS() : shortData;
  }, [projectDetails, shortId, shortData]);

  const hasReframeSrc = useMemo(() => {
    if (!resolvedShort) return false;
    return !!resolvedShort.reframeSrc
      || resolvedShort.clips?.some((clip) => clip.isReframe && !!clip.reframeSrc);
  }, [resolvedShort]);

  const isReframeThumbnailCaptured = resolvedShort?.isReframeThumbnailCaptured === true;

  const isResolvedReframeShort = !!resolvedShort?.isReframe
    || resolvedShort?.clips?.some((clip) => clip.isReframe);

  const hasReframeSceneData = useMemo(() => {
    if (!isResolvedReframeShort || !appReframeData) return false;

    const workspaceItems = projectDetails.get
      ? projectDetails.get("workspaceItems")
      : projectDetails.workspaceItems;
    if (!workspaceItems) return false;

    const videoItem = workspaceItems.valueSeq
      ? workspaceItems.valueSeq().find((item) => (item.get ? item.get("type") : item.type) === "UPVIDEO")
      : Object.values(workspaceItems).find((item) => item.type === "UPVIDEO");
    const baseAssetId = videoItem
      ? (videoItem.get ? videoItem.get("assetId") : videoItem.assetId)
      : null;
    if (!baseAssetId) return false;

    const reframeDataJS = appReframeData.toJS ? appReframeData.toJS() : appReframeData;
    const compositeKey = `${baseAssetId}_${shortId}`;
    const entry = reframeDataJS[compositeKey]
      || reframeDataJS[baseAssetId]
      || (reframeDataJS.assetId === baseAssetId ? reframeDataJS : null);

    return !!(entry && entry.scenes && entry.scenes.length);
  }, [isResolvedReframeShort, appReframeData, projectDetails, shortId]);

  const resumePlaybackAfterThumbnail = useCallback(() => {
    const playerId = `animo-player-${shortId}`;
    const startTime = resolvedShort?.start ?? (shortData?.get ? shortData.get("start") : shortData?.start) ?? 0;

    pendingPlaybackResumeRef.current = true;

    playbackResumeTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    playbackResumeTimersRef.current = [];

    const queuePlaybackRestart = (delayMs) => {
      const timerId = window.setTimeout(() => {
        // Child store only — ShortsWrapper has an isolated player store.
        dispatch(setPlayAll(false, false));
        window.setTimeout(() => {
          dispatch(setPlayHeadTime({ playhead: startTime }));
          dispatch(setPlayAll(true, false));
          window.setTimeout(() => {
            dispatch(playPausePlayer({ playhead: startTime, isPause: false }));
            setShortsContext(SET_ACTIVE_PLAYER, playerId);
          }, 300);
        }, 150);
      }, delayMs);
      playbackResumeTimersRef.current.push(timerId);
    };

    queuePlaybackRestart(500);
    queuePlaybackRestart(1600);
    queuePlaybackRestart(3000);
  }, [dispatch, shortId, setShortsContext, resolvedShort, shortData]);

  useEffect(() => {
    const prevT1 = prevT1ForResumeRef.current;
    prevT1ForResumeRef.current = t1;

    if (!pendingPlaybackResumeRef.current || !t1 || t1 === prevT1) {
      return;
    }

    const playerId = `animo-player-${shortId}`;
    const startTime = resolvedShort?.start ?? (shortData?.get ? shortData.get("start") : shortData?.start) ?? 0;

    dispatch(setPlayHeadTime({ playhead: startTime }));
    dispatch(playPausePlayer({ playhead: startTime, isPause: false }));
    setShortsContext(SET_ACTIVE_PLAYER, playerId);
    pendingPlaybackResumeRef.current = false;
    playbackResumeTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    playbackResumeTimersRef.current = [];
  }, [t1, dispatch, shortId, resolvedShort, shortData, setShortsContext]);

  useEffect(() => () => {
    playbackResumeTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    playbackResumeTimersRef.current = [];
  }, []);

  const _onShortThumbnailSuccess = useCallback(async (blob) => {
    if (!blob || !shortId || !userId || !projectId) return;
    const startTime = resolvedShort?.start ?? (shortData?.get ? shortData.get("start") : shortData?.start) ?? 0;
    try {
      const key = `a/u/${userId}/shortsThumbnail/${projectId}/${shortId}.png`;
      const awsAccess = await getAwsAccess(userId);
      await uploadFile(awsAccess, blob, key);
      const thumbnailPath = `${userId}/shortsThumbnail/${projectId}/${shortId}.png`;
      const shortsMap = projectDetails.get ? projectDetails.get("shorts") : projectDetails.shorts;
      if (!shortsMap) return;
      const shortsList = shortsMap.toJS ? shortsMap.toJS() : { ...shortsMap };
      if (!shortsList[shortId]) return;

      // Tear down child-store player before parent metadata update triggers rebuild.
      dispatch(setPlayAll(false, false));
      dispatch(playPausePlayer({ playhead: startTime, isPause: true }));
      setShortsContext(SET_ACTIVE_PLAYER, null);

      (parentDispatch || dispatch)(
        updateProjectData({
          shorts: {
            ...shortsList,
            [shortId]: {
              ...shortsList[shortId],
              thumbnail_url: thumbnailPath,
              isReframeThumbnailCaptured: true,
            },
          },
        })
      );
      resumePlaybackAfterThumbnail();
    } catch (e) {
      didShortThumbnailRef.current = false;
      pendingPlaybackResumeRef.current = false;
    }
  }, [
    shortId,
    userId,
    projectId,
    projectDetails,
    parentDispatch,
    dispatch,
    resumePlaybackAfterThumbnail,
    resolvedShort,
    shortData,
    setShortsContext,
  ]);

  const onShortThumbnailSuccess = useCustomCallback({
    memoizedCallback: _onShortThumbnailSuccess,
  });

  const isReframeReadyForThumbnail = isResolvedReframeShort && hasReframeSrc && hasReframeSceneData;

  const captureShortThumbnail = useCallback(async () => {
    const blob = await captureShortReframeThumbnail({
      projectDetails,
      shortData: resolvedShort,
      appReframeData,
      playheadOffset: 5,
      maxRes: 352,
    });
    if (blob) {
      await onShortThumbnailSuccess(blob);
      return true;
    }
    return false;
  }, [projectDetails, resolvedShort, appReframeData, onShortThumbnailSuccess]);

  const scheduleThumbnailCapture = useCallback((delayMs = 4000) => {
    if (didShortThumbnailRef.current) return;
    setTimeout(async () => {
      if (didShortThumbnailRef.current) return;
      didShortThumbnailRef.current = true;
      try {
        const captured = await captureShortThumbnailRef.current?.();
        if (!captured) {
          didShortThumbnailRef.current = false;
        }
      } catch (e) {
        didShortThumbnailRef.current = false;
      }
    }, delayMs);
  }, []);

  useEffect(() => {
    captureShortThumbnailRef.current = captureShortThumbnail;
    scheduleThumbnailCaptureRef.current = scheduleThumbnailCapture;
  }, [captureShortThumbnail, scheduleThumbnailCapture]);

  // Fallback for already-reframed shorts opened in modal (no in-session reframe click)
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    if (
      isReframeReadyForThumbnail &&
      !isReframeLoading &&
      !showProgressModal &&
      !reframeApplied &&
      !didShortThumbnailRef.current &&
      shortId &&
      !isReframeThumbnailCaptured
    ) {
      timer = setTimeout(async () => {
        if (cancelled || didShortThumbnailRef.current) return;
        scheduleThumbnailCaptureRef.current?.(0);
      }, 2500);
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [
    isReframeReadyForThumbnail,
    reframeApplied,
    isReframeLoading,
    showProgressModal,
    isReframeThumbnailCaptured,
    hasReframeSrc,
    hasReframeSceneData,
    shortId,
  ]);

  const didOptimisticUpdateRef = useRef(false);
  const applyOptimisticUpdateRef = useRef(null);

  const applyOptimisticUpdate = useCallback((isReframe) => {
    const shortsMap = projectDetails ? (projectDetails.get ? projectDetails.get("shorts") : projectDetails.shorts) : null;
    const shortsList = shortsMap ? (shortsMap.toJS ? shortsMap.toJS() : shortsMap) : {};
    const shortId = shortData.get ? shortData.get("id") : shortData.id;
    const matchedShort = shortsList[shortId];
    if (matchedShort) {
      const updatedShortsMap = { ...shortsList };
      const updatedShort = { ...matchedShort };
      if (updatedShort.clips && updatedShort.clips.length > 0) {
        updatedShort.clips = updatedShort.clips.map((clip) => ({ ...clip, isReframe }));
      } else {
        updatedShort.isReframe = isReframe;
      }
      updatedShortsMap[updatedShort.id] = updatedShort;
      (parentDispatch || dispatch)(updateProjectData({ shorts: updatedShortsMap }));
    }
  }, [shortData, projectDetails, parentDispatch, dispatch]);

  useEffect(() => {
    applyOptimisticUpdateRef.current = applyOptimisticUpdate;
  }, [applyOptimisticUpdate]);

  useEffect(() => {
    return () => {
      cancelReframeRef.current = true;
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      // Revert optimistic update if it was started but not finalized
      if (didOptimisticUpdateRef.current && applyOptimisticUpdateRef.current) {
        applyOptimisticUpdateRef.current(false);
      }
      // Clear reframe loading flag in context on unmount
      setShortsContext(SET_REFRAME_LOADING, false);
      setShortsContext(SET_REFRAME_PROGRESS, 0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Broadcast isReframeLoading to ShortsContext so siblings (e.g. the play button
  // in ShortsPlayerControlPanel) can disable themselves while reframe is in progress.
  useEffect(() => {
    setShortsContext(SET_REFRAME_LOADING, isReframeLoading);
    if (!isReframeLoading) {
      setShortsContext(SET_REFRAME_PROGRESS, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReframeLoading]);

  useEffect(() => {
    if (isReframeLoading) {
      setShortsContext(SET_REFRAME_PROGRESS, reframeProgress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reframeProgress, isReframeLoading]);

  // Automatically fetch reframe JSON files for any reframed shorts and dispatch to Redux reframeData store
  useEffect(() => {
    if (!projectDetails) return;

    const workspaceItems = projectDetails.get
      ? projectDetails.get("workspaceItems")
      : projectDetails.workspaceItems;
    if (!workspaceItems) return;

    const videoItem = workspaceItems.valueSeq
      ? workspaceItems.valueSeq().find((item) => (item.get ? item.get("type") : item.type) === "UPVIDEO")
      : Object.values(workspaceItems).find((item) => item.type === "UPVIDEO");

    const assetId = videoItem ? (videoItem.get ? videoItem.get("assetId") : videoItem.assetId) : null;

    const shortsMap = projectDetails.get
      ? projectDetails.get("shorts")
      : projectDetails.shorts;
    if (!shortsMap) return;

    const short = shortData.toJS ? shortData.toJS() : shortData;
    const clips = short.clips || [];
    if (clips.length > 0) {
      clips.forEach((clip) => {
        if (clip.isReframe && clip.reframeSrc) {
          const url = clip.reframeSrc;

          if (!processedReframeClipsRef.current.has(url)) {
            processedReframeClipsRef.current.add(url);
            fetch(url)
              .then((res) => res.json())
              .then((reframeJson) => {
                // Only write to the shared reframeData store with a valid composite
                // key (`assetId_shortId`). Dispatching a reframeJson WITHOUT an assetId
                // takes the reducer's full-replace branch, leaving reframeData in a
                // top-level `{ scenes }` shape — which both hides the keyed data the
                // player looks up AND causes the next keyed write to wipe the store.
                if (reframeJson && typeof reframeJson === "object" && assetId) {
                  reframeJson.assetId = `${assetId}_${short.id}`;
                  (parentDispatch || dispatch)(setReframeData(reframeJson));
                } else {
                  // assetId not resolvable yet — allow a retry on a later render.
                  processedReframeClipsRef.current.delete(url);
                }
              })
              .catch(() => {
                processedReframeClipsRef.current.delete(url);
              });
          }
        }
      });
    } else if (short.isReframe && short.reframeSrc) {
      const url = short.reframeSrc;
      if (!processedReframeClipsRef.current.has(url)) {
        processedReframeClipsRef.current.add(url);
        fetch(url)
          .then((res) => res.json())
          .then((reframeJson) => {
            // See note above: never write reframe JSON to the store without a
            // valid composite key, or it poisons/wipes the keyed reframeData.
            if (reframeJson && typeof reframeJson === "object" && assetId) {
              reframeJson.assetId = `${assetId}_${short.id}`;
              (parentDispatch || dispatch)(setReframeData(reframeJson));
            } else {
              processedReframeClipsRef.current.delete(url);
            }
          })
          .catch(() => {
            processedReframeClipsRef.current.delete(url);
          });
      }
    }
  }, [projectDetails, dispatch, parentDispatch]);

  const handleCancelProgress = () => {
    cancelReframeRef.current = true;
    setShowProgressModal(false);
    setIsReframeLoading(false);
    setReframeProgress(0);
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const pausePlayer = useCallback(() => {
    const start = shortData.get("start");
    let playhead = start;
    if (t1 && typeof t1.time === "function") {
      playhead = t1.time();
    }
    dispatch(playPausePlayer({ playhead, isPause: true }));
    dispatch(setPlayAll(false, false));
    setShortsContext(SET_ACTIVE_PLAYER, null);
  }, [dispatch, shortData, t1, setShortsContext]);

  const resumePlayer = useCallback(() => {
    dispatch(setPlayAll(true, false));
    setShortsContext(SET_ACTIVE_PLAYER, null);
  }, [dispatch, setShortsContext]);

  const handleAiReframe = useCallback((e) => {
    e?.stopPropagation?.();
    trackerEvents(TRACKERS.LFTS_AI_REFRAME_APPLY_BTN_CLICKED, {
      projectId,
      isBoth: 1,
      shortId: shortData.get ? shortData.get("id") : shortData.id,
    });

    // Lite + Asia / listed countries → upgrade instead of applying reframe
    if (isReframeRestricted && showPricingModal) {
      pausePlayer();
      trackerEvents(TRACKERS.LFTS_REFRAME_LITE_UPGRADE, {
        projectId,
        shortId: shortData.get ? shortData.get("id") : shortData.id,
        isBoth: 1,
      });
      showPricingModal({
        type: "reframeApply",
        source: TRACKERS.LFTS_REFRAME_LITE_UPGRADE,
        shortId: shortData.get ? shortData.get("id") : shortData.id,
      });
      return;
    }

    didShortThumbnailRef.current = false;
    setIsReframeLoading(true);
    setShortsContext(SET_REFRAME_LOADING, true);
    setShortsContext(SET_REFRAME_PROGRESS, 8);
    // Stop the shorts player
    const start = shortData.get ? shortData.get("start") : shortData.start;
    dispatch(playPausePlayer({ playhead: start, isPause: true }));
    dispatch(setPlayAll(false, false));
    setShortsContext(SET_ACTIVE_PLAYER, null);
    const assetId = shortData.get ? shortData.get("assetId") : shortData.assetId;


    let reframeErrorShown = false;
    const showReframeErrorModal = (res) => {
      if (reframeErrorShown) return;
      reframeErrorShown = true;
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setShowProgressModal(false);
      setIsReframeLoading(false);
      didOptimisticUpdateRef.current = false;
      applyOptimisticUpdate(false);
      resumePlayer();

      const code = res?.code;
      const errorByCode = {
        RF001: { title: content.RF001_TITLE, message: content.RF001 },
        RF002: { title: content.RF002_TITLE, message: content.RF002 },
        RF003: { title: content.RF003_TITLE, message: content.RF003 },
        RF004: { title: content.RF004_TITLE, message: content.RF004 },
        RF005: { title: content.RF005_TITLE, message: content.RF005 },
        RF006: { title: content.RF006_TITLE, message: content.RF006 },
      };
      const { title, message } = errorByCode[code] || {
        title: content.RF001_TITLE,
        message: content.RF001,
      };

      setErrorModal({
        show: true,
        title,
        message,
        onRetry: code === "RF002"
          ? () => {
            setErrorModal({ show: false, title: "", message: "", onRetry: null });
            handleAiReframe();
          }
          : null,
      });
    };

    const pollReframeStatus = async (jobId, retryCount = 0, maxRetries = 3000) => {
      if (cancelReframeRef.current) return false;
      try {
        const res = await reframeStatusApi(jobId);
        if (cancelReframeRef.current) return false;

        if (res?.error) {
          showReframeErrorModal(res);
          return false;
        }

        if (res.status === "COMPLETED") {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          setReframeProgress(100);
          setShowProgressModal(false);
          return res;
        }

        if (res.status === "Failed" || res.status === "FAILED") {
          showReframeErrorModal(res);
          return false;
        }

        if (retryCount < maxRetries) {
          return new Promise(resolve => {
            setTimeout(() => {
              resolve(pollReframeStatus(jobId, retryCount + 1, maxRetries));
            }, 3000);
          });
        }

        showReframeErrorModal(res);
        return false;
      } catch (error) {
        if (cancelReframeRef.current) return false;
        if (retryCount < maxRetries) {
          return new Promise(resolve => {
            setTimeout(() => {
              resolve(pollReframeStatus(jobId, retryCount + 1, maxRetries));
            }, 2000);
          });
        }
        showReframeErrorModal(error);
        return false;
      }
    };

    const clipsData = shortData.get ? shortData.get("clips") : shortData.clips;
    const finalUserId = userId || "";
    const finalTeamId = teamId || currentTeamId || "";
    const shortIdVal = shortData.get
      ? (shortData.get("short_id") || shortData.get("id"))
      : (shortData.short_id || shortData.id);

    let payloadClips = [];
    if (clipsData && (clipsData.size > 0 || clipsData.length > 0)) {
      const clipsList = clipsData.toArray ? clipsData.toArray() : Array.from(clipsData);
      payloadClips = clipsList.map((clip) => {
        const cStart = clip.get ? clip.get("startTime") : clip.startTime || 0;
        const cEnd = clip.get ? clip.get("endTime") : clip.endTime || 0;
        const cId = clip.get
          ? (clip.get("clip_id") || clip.get("clipId") || clip.get("id"))
          : (clip.clip_id || clip.clipId || clip.id);

        return {
          short_id: shortIdVal || "",
          clip_id: cId || "",
          start_time: Number(cStart),
          end_time: Number(cEnd)
        };
      });
    } else {
      const start = shortData.get ? shortData.get("start") : shortData.start;
      const end = shortData.get ? shortData.get("end") : shortData.end;
      payloadClips = [
        {
          short_id: shortIdVal || "",
          clip_id: shortIdVal ? `clip_${shortIdVal}` : "",
          start_time: Number(start || 0),
          end_time: Number(end || 0)
        }
      ];
    }
    const payload = {
      assetId,
      userId: finalUserId,
      teamId: finalTeamId,
      clips: payloadClips,
    };

    cancelReframeRef.current = false;

    const processReframeResult = (resData) => {
      if (!resData || !resData.clips || resData.clips.length === 0) return;

      const shortsMap = projectDetails ? (projectDetails.get ? projectDetails.get("shorts") : projectDetails.shorts) : null;
      const shortsList = shortsMap ? (shortsMap.toJS ? shortsMap.toJS() : shortsMap) : {};

      const activeShort = shortData.toJS ? shortData.toJS() : shortData;
      const updatedShort = { ...activeShort };
      const sourceClips = resData.clips;

      const resolveSrcUrl = (sourceClip, fallbackSrc) => {
        const src = sourceClip
          ? (sourceClip.reframeSrc || sourceClip.final_preview || "")
          : (fallbackSrc || "");
        if (src && !src.startsWith("http")) {
          const base = REFRAME_DOMAIN.endsWith("/") ? REFRAME_DOMAIN.slice(0, -1) : REFRAME_DOMAIN;
          const path = src.startsWith("/") ? src : `/${src}`;
          return `${base}${path}`;
        }
        return src;
      };

      const reframeDataFetches = [];

      const fetchReframeData = (url, shortIdVal) => {
        if (!url) return;
        const fetchPromise = fetch(url)
          .then((res) => res.json())
          .then((reframeJson) => {
            if (reframeJson && typeof reframeJson === "object" && assetId) {
              reframeJson.assetId = `${assetId}_${shortIdVal}`;
              (parentDispatch || dispatch)(setReframeData(reframeJson));
            }
          })
          .catch(() => { });
        reframeDataFetches.push(fetchPromise);
      };

      if (updatedShort.clips && updatedShort.clips.length > 0) {
        // Sort both arrays by end time and match them by sorted index
        const sortedLocalClips = [...updatedShort.clips].sort((a, b) => (a.endTime || 0) - (b.endTime || 0));
        const sortedSourceClips = [...sourceClips].sort((a, b) => (a.end_time || 0) - (b.end_time || 0));

        const indexMatchMap = new Map();
        sortedLocalClips.forEach((lc, idx) => {
          const sc = sortedSourceClips[idx];
          if (sc) {
            indexMatchMap.set(lc, sc);
          }
        });

        updatedShort.clips = updatedShort.clips.map((clip) => {
          const matchedSourceClip = indexMatchMap.get(clip) || sourceClips[0];
          return {
            ...clip,
            isReframe: true,
            reframeSrc: resolveSrcUrl(matchedSourceClip, clip.reframeSrc)
          };
        });
      } else {
        // Single short (no clips array) - take the first source clip
        updatedShort.isReframe = true;
        updatedShort.reframeSrc = resolveSrcUrl(sourceClips[0], updatedShort.reframeSrc);
      }

      const updatedShortsMap = { ...shortsList };
      updatedShortsMap[updatedShort.id] = updatedShort;

      (parentDispatch || dispatch)(updateProjectData({ shorts: updatedShortsMap }));

      // Immediately fetch the reframe JSON contents to update Redux local appReframeData
      const clips = updatedShort.clips || [];
      if (clips.length > 0) {
        clips.forEach((clip) => {
          if (clip.isReframe && clip.reframeSrc) {
            fetchReframeData(clip.reframeSrc, updatedShort.id);
          }
        });
      } else if (updatedShort.isReframe && updatedShort.reframeSrc) {
        fetchReframeData(updatedShort.reframeSrc, updatedShort.id);
      }

      // After all clip JSON files load, capture thumbnail (in-session reframe button flow)
      if (reframeDataFetches.length > 0) {
        Promise.all(reframeDataFetches).finally(() => {
          scheduleThumbnailCaptureRef.current?.(4000);
        });
      }
    };
    // Immediately pause the player so neither video nor subtitle plays
    // while the AI Reframe API call and/or polling is in progress.
    pausePlayer();

    applyOptimisticUpdate(true); // mark isReframe: true immediately
    didOptimisticUpdateRef.current = true;

    post(`${API_URL}${API.AI_REFRAME}`, payload, false).then(async (res) => {
      if (cancelReframeRef.current) return;

      if (res?.error) {
        showReframeErrorModal(res);
        return;
      }

      if (res?.status === "Queued" || res?.status === "QUEUED" || res?.status === "IN_PROGRESS") {
        setShowProgressModal(true);
        setReframeProgress(0);
        // Manual progress increment: 20 → 40 → 60 → 90
        progressIntervalRef.current = setInterval(() => {
          setReframeProgress((prev) => {
            if (prev >= 90) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
              return 90;
            }
            if (prev < 20) return 20;
            if (prev < 40) return 40;
            if (prev < 60) return 60;
            return 90;
          });
        }, 2000);
        const completedData = await pollReframeStatus(res.job_id);
        if (cancelReframeRef.current) return;
        if (!completedData) {
          if (!reframeErrorShown) {
            showReframeErrorModal(completedData);
          }
          return;
        }
        setIsReframeLoading(false);
        didOptimisticUpdateRef.current = false;
        processReframeResult(completedData);
        setReframeApplied(true);
        success(content.AI_REFRAME_APPLIED_SUCCESSFULLY);
        // Player will auto-restart via the setPlayAll(true) dispatched in shorts-player.jsx
        // once appReframeData updates and the project rebuilds.
      } else {
        setIsReframeLoading(false);
        if (!res.error) {
          didOptimisticUpdateRef.current = false;
          processReframeResult(res);
          setReframeApplied(true);
          success(content.AI_REFRAME_APPLIED_SUCCESSFULLY);
          // Player will auto-restart once appReframeData updates and the project rebuilds.
        } else {
          showReframeErrorModal(res);
        }
      }
    }).catch((err) => {
      if (cancelReframeRef.current) return;
      processReframeResult(null);
      showReframeErrorModal(err);
    });
  }, [
    shortData, dispatch, setShortsContext, pausePlayer, applyOptimisticUpdate,
    post, resumePlayer, projectDetails, parentDispatch, success, userId,
    teamId, currentTeamId, projectId, isReframeRestricted, showPricingModal
  ]);

  const [isExportLoading, setIsExportLoading] = useState(false);
  const closeModalExport = () => setIsModal(false);

  const openPreset = () => {
    if (hasSubtitle) {
      pausePlayer();
      setShortsContext(OPEN_SHORT_PRESET, true);
    }
  };

  const trackPlanUpgraded = useCallback((source) => {
    trackerEvents(TRACKERS.PLAN_UPGRADED, {
      projectId,
      shortId: shortData.get("id"),
      source,
      isBoth: 1,
    });
  }, [projectId, shortData]);

  const handleFeatureClick = (e, feature) => {
    e.stopPropagation();
    if (feature.disabled) return;

    pausePlayer();

    if (feature.opensPreset) {
      if (hasSubtitle) {
        openPreset();
      } else if (showPricingModal) {
        trackPlanUpgraded(feature.upgradeSource);
        showPricingModal({
          type: feature.upgradeType,
          source: feature.upgradeSource,
          shortId: shortData.get("id"),
        });
      }
      return;
    }

    if (!showPricingModal) return;
    trackPlanUpgraded(feature.upgradeSource);
    showPricingModal({
      type: feature.upgradeType,
      source: feature.upgradeSource,
      shortId: shortData.get("id"),
    });
  };

  const openNewProject = useCallback((shortId, isExport = false) => {
    pausePlayer();
    if (!isExport) {
      trackerEvents(TRACKERS.LTSF_PROJECT_EDITED, {
        projectId,
        shortId,
        isBoth: 1
      });
    }
    let pathName = "";
    if (isExport) {
      pathName = "render/";
      setIsExportLoading(true);
    } else {
      pathName = ENVIRONMENT === "LOCAL" ? "" : "editproject/";
      setIsLoading(true);
    }
    post(`${API_URL}${API.SHORTS.CREATE}`, { projectId, shortId, isExport }).then((res) => {
      if (!res.error) {
        openUrl(`${SITE_URL}${pathName + res.projectId}`);
        setIsLoading(false);
        setIsExportLoading(false);
      } else {
        setIsLoading(false);
        setIsExportLoading(false);
        setErrorModal({
          show: true,
          title: isExport ? "Export Failed" : "Project Creation Failed",
          message: res.message || "Something went wrong. Please try again.",
          onRetry: () => {
            setErrorModal({ show: false, title: "", message: "", onRetry: null });
            openNewProject(shortId, isExport);
          },
        });
      }
    }).catch((err) => {
      setIsLoading(false);
      setIsExportLoading(false);
      setErrorModal({
        show: true,
        title: isExport ? "Export Failed" : "Project Creation Failed",
        message: err?.message || "An error occurred. Please try again.",
        onRetry: () => {
          setErrorModal({ show: false, title: "", message: "", onRetry: null });
          openNewProject(shortId, isExport);
        },
      });
    });
  },
    [projectId, post, pausePlayer]
  );

  const handleExportClick = useCallback(() => {
    const shortId = shortData.get("id");
    if (isShortsFlowSourceType(sourceType)
      && isReframeRestricted
      && isReframeShort
      && showPricingModal
    ) {
      pausePlayer();
      trackerEvents(TRACKERS.LFTS_REFRAME_LITE_UPGRADE, {
        projectId,
        shortId: shortData.get("id"),
        isBoth: 1,
      });
      showPricingModal({ type: "reframe", source: TRACKERS.LFTS_REFRAME_LITE_UPGRADE });
      return;
    }
    openNewProject(shortId, true);
  }, [shortData, sourceType, isReframeRestricted, isReframeShort, showPricingModal, openNewProject, pausePlayer, projectId]);

  const truncate = (str, length) => {
    if (str.length > length) {
      return `${str.substr(0, length)}...`;
    }
    return str;
  };

  const clips = shortData.get("clips");
  const hasClips = clips && clips.size > 0;

  const renderBody = () => {
    if (hasClips) {
      return clips.map((clip, index) => {
        const startTime = clip.get("startTime");
        const endTime = clip.get("endTime");
        const clipText = clip.get("text") || clip.get("transcript") || "";
        return (
          // eslint-disable-next-line react/no-array-index-key
          <ClipEntry key={`${index}-${startTime}`} >
            <ClipText color={vmTheme[theme].subtitleTextColor}>
              <span style={{ font: font.boldBase_21 }}>[{secondsToMinute(startTime)} - {secondsToMinute(endTime)}]</span> {clipText}
            </ClipText>
          </ClipEntry>
        );
      });
    }
    return (
      <ClipEntry>
        <span style={{ font: font.boldBase_21 }}>[{secondsToMinute(shortData.get("start"))} - {secondsToMinute(shortData.get("end"))}]</span>
        <Typography
          innerContent={shortData.get("phrase") || ""}
          color={vmTheme[theme].subtitleTextColor}
          font={font.normal_21}
          enableTrim={false}
        />
      </ClipEntry>
    );
  };

  return (
    <>
      {errorModal.show && (
        <ErrorModal
          title={errorModal.title}
          message={errorModal.message}
          primaryButtonText={errorModal.onRetry ? content.TRY_AGAIN : undefined}
          secondaryButtonText={content.CLOSE}
          onPrimaryButtonClick={errorModal.onRetry ? errorModal.onRetry : undefined}
          onSecondaryButtonClick={() => setErrorModal({ show: false, title: "", message: "", onRetry: null })}
          onCloseModal={() => setErrorModal({ show: false, title: "", message: "", onRetry: null })}
        />
      )}
      <ContentContainer>
        <PanelOverlay $showReframeLoading={isPanelBlocked} />
        <ContentHeader>
          {(shortData.get("category") || shortData.get("confidenceScore")) && (
            <MetaRow>
              {shortData.get("category") && (
                <Typography innerContent={shortData.get("category").charAt(0).toUpperCase() + shortData.get("category").slice(1)} font={font.medium_12} color={'#5B5F70'} padding={'3px 8px'}
                  textStyles={{ background: '#EBECF0', borderRadius: '50px' }}
                />
              )}
            </MetaRow>
          )}
          <Export
            modalHandler={closeModalExport}
            isShowModal={isModal}
            projectTitle={truncate(shortData.get("title"), 40)}
            onCloseModal={() => setIsModal(false)}
            shortId={shortData.get("id")}
          />
        </ContentHeader>
        <FlexDiv justifyContent="space-between" gap="12px">
          <Typography
            content={shortData.get("title")}
            font={font.normal_600_16}
            color={vmTheme[theme].shortsSubText}
            padding="0px 0px 0px"
            enableTrim={false}
            textStyles={{ flex: 1, minWidth: 0, lineHeight: '1.4' }}
          />
          {inModal && permission !== 0 && (
            <ShortsFeedbackButtons
              feedbackType={feedbackType}
              tooltipIdSuffix={shortData.get("id")}
              tooltipContext="modal"
              variant="onLight"
              onLike={() => {
                trackerEvents(TRACKERS.LFSF_VIDEO_LIKED, {
                  projectId,
                  shortId: shortData.get("id"),
                  isBoth: 1
                });
                onLike?.();
              }}
              onDislike={() => {
                onDislike?.();
              }}
            />
          )}
        </FlexDiv>
        <FlexDiv style={{ marginTop: '16px', paddingBottom: '16px' }}>
          {shortData.get("confidenceScore") ? (
            <Typography
              content={`${Math.round(shortData.get("confidenceScore"))}%`}
              font={font.bold_24_900}
              enableTrim={false}
              color={vmTheme[theme].shortsColor}
              padding={'3px 14px'}
              textStyles={{
                borderRadius: '100px',
                background: '#DBF8ED'
              }}
            />
          ) : null}
          <FlexDiv justifyContent="flex-end" gap="12px">
            {plan !== PLAN.LITE && permission !== 0 && shortData.get("confidenceScore") ? <CustomTooltip
              tooltipId="shorts-subtitle-btn-tt"
              dataTooltip={content.SUBTITLE_STYLE}
              font={font.normal_medium_12}
              tooltipPosition="top"
              delayVisibilityBy={0}
            >
              <Action
                data-tooltip-id="shorts-subtitle-btn-tt"
                text={''}
                font={font.mediumBase_21}
                className="subtitle-btn"
                marginTop="0px"
                fontColor={vmTheme[theme].shortsSubText}
                marginBottom="0px"
                width="52px"
                height={"36px"}
                borderRadius={"8px"}
                borderWidth={"1px"}
                customBeforeIcon={`${STATIC_PATH}subtitle.svg`}
                borderColor={vmTheme[theme].shortsBorderColor}
                background="transparent"
                onClick={() => openPreset()}
                isDisabled={isPanelBlocked || !hasSubtitle}
              />
            </CustomTooltip> : null}
            {permission !== 0 && (
              <CustomTooltip
                tooltipId="shorts-edit-project-tt"
                font={font.normal_medium_12}
                dataTooltip={content.EDIT_AS_PROJECT}
                tooltipPosition="top"
                delayVisibilityBy={0}
              >
                <Action
                  data-tooltip-id="shorts-edit-project-tt"
                  text={''}
                  font={font.mediumBase_21}
                  className="edit-project"
                  marginTop="0px"
                  fontColor={vmTheme[theme].shortsSubText}
                  marginBottom="0px"
                  width="52px"
                  height={"36px"}
                  borderRadius={"8px"}
                  borderWidth={"1px"}
                  customBeforeIcon={`${STATIC_PATH}vm-cut.svg`}
                  iconWidth="20px"
                  iconHeight="20px"
                  borderColor={vmTheme[theme].shortsBorderColor}
                  background="transparent"
                  onClick={() => openNewProject(shortData.get("id"))}
                  isLoading={isLoading}
                  isDisabled={isPanelBlocked}
                />
              </CustomTooltip>
            )}
            {permission !== 0 && (
              <>
                <Divider />
                <Action
                  text={content.EXPORT}
                  font={font.mediumBase_21}
                  className="export-project"
                  marginTop="0px"
                  fontColor={vmTheme[theme].shortsSecondaryColor}
                  marginBottom="0px"
                  width="99px"
                  height={"36px"}
                  borderRadius={"8px"}
                  background={vmTheme[theme].shortsBtnBgColor}
                  onClick={handleExportClick}
                  isLoading={isExportLoading}
                  isDisabled={isPanelBlocked}
                />
              </>)}
          </FlexDiv>
        </FlexDiv>

        <ContentBody $inModal={inModal} $isPanelBlocked={isPanelBlocked} style={{ position: 'relative' }}>
          {isReframeRequired && dimensionName === "vertical" && (
            <ReframeSection align={((showProgressModal || isReframeLoading) || (reframeApplied || isReframeShort)) ? 'center' : 'start'}>
              <ReframeIcon src={`${STATIC_PATH}ai-reframe.svg`} alt="AI Reframe" />
              {showProgressModal || isReframeLoading ? (
                <ReframeContentCol key="progress">
                  <ReframeTextRow>
                    <Typography
                      content={content.GENERATING_AI_REFRAME}
                      font={font.boldBase_21}
                      color="#313345"
                      enableTrim={false}
                    />
                    <Typography
                      content={`${reframeProgress}%`}
                      font={font.normal_600_16}
                      color="#5028FF"
                      enableTrim={false}
                    />
                  </ReframeTextRow>
                  <ReframeInlineProgress>
                    <ReframeInlineProgressBar />
                    <ReframeInlineProgressFill style={{ width: `${reframeProgress}%` }} />
                  </ReframeInlineProgress>
                </ReframeContentCol>
              ) : (reframeApplied || isReframeShort) ? (
                <ReframeContentCol key="applied">
                  <Typography
                    content="AI Reframe Applied"
                    font={font.normal14}
                    color="#313345"
                    enableTrim={false}
                  />
                </ReframeContentCol>
              ) : (
                <ReframeContentCol key="default">
                  <Typography
                    content={content.THIS_CLIP_COULD_LOOK}
                    font={font.normal14}
                    color="#313345"
                    enableTrim={false}
                  />
                  <Action
                    text={isReframeRestricted ? content.GET_AI_REFRAME : content.APPLY_REFRAME}
                    font={font.normal14}
                    marginTop="0px"
                    fontColor="#ffffff"
                    marginBottom="0px"
                    width="fit-content"
                    height="32px"
                    borderRadius="50px"
                    background={isReframeRestricted
                      ? vmTheme[theme].btnLinearColor
                      : "linear-gradient(94deg, #AC67FD 0%, #775AFD 100%)"}
                    hoverColor={isReframeRestricted
                      ? vmTheme[theme].btnHoverLinearColor
                      : undefined}
                    onClick={handleAiReframe}
                    isLoading={isReframeLoading}
                    isDisabled={isPanelBlocked}
                    className="ai-reframe-btn"
                    customBeforeIcon={isReframeRestricted ? `${STATIC_PATH}white-star.svg` : `${STATIC_PATH}ai-star.svg`}
                    iconWidth="20px"
                    iconHeight="20px"
                    style={{ padding: isReframeLoading ? '0px 22px' : '0px 22px 0px 46px' }}
                  />
                </ReframeContentCol>
              )}
            </ReframeSection>
          )}

          {plan === PLAN.LITE ? <div>
            <HdBanner>
              <HdBadge>
                {/* <img src={`${STATIC_PATH}hd-icon.svg`} alt="HD" /> */}
              </HdBadge>
              <HdBannerText>
                <FormattedMessage id={content.SHORTS_FREE_EXPORT_NOTE} />
                <HdBannerLink onClick={() => {
                  pausePlayer();
                  trackPlanUpgraded(PRICING_MODAL_ARGS.source);
                  showPricingModal({
                    ...PRICING_MODAL_ARGS,
                    shortId: shortData.get("id"),
                  });
                }}>
                  {/* <FormattedMessage id={content.SHORTS_GO_HD} /> */}
                </HdBannerLink>
              </HdBannerText>
            </HdBanner>

            <ViralSection>
              <ViralHeader>
                <ViralTitleRow>
                  <img src={`${STATIC_PATH}bolt.svg`} alt="Viral" />
                  <Typography
                    content={content.SHORTS_MAKE_IT_VIRAL}
                    font={font.boldBase_16}
                    color="#5028FF"
                    enableTrim={false}
                  />
                </ViralTitleRow>
              </ViralHeader>
              <ViralFeatureGrid>
                {VIRAL_FEATURES.map((feature) => {
                  const tooltipId = `shorts-viral-${feature.id}-tt`;
                  return (
                    <CustomTooltip
                      key={feature.id}
                      tooltipId={tooltipId}
                      dataTooltip={feature.description}
                      tooltipPosition="top"
                      font={font.normal_14}
                      delayVisibilityBy={0}
                    >
                      <ViralFeatureCardWrap>
                        {feature.badgeVariant && (
                          <ViralFeatureBadge $variant={feature.badgeVariant}>
                            {feature.badgeVariant === "comingSoon" ? (
                              <Typography
                                content={feature.badge}
                                font={font.mediumMicro_10}
                                color="#ffffff"
                                enableTrim={false}
                              />
                            ) : (
                              <img
                                src={`${STATIC_PATH}${feature.badgeVariant}.svg`}
                                alt=""
                              />
                            )}
                          </ViralFeatureBadge>
                        )}
                        <ViralFeatureCard
                          type="button"
                          disabled={feature.disabled}
                          data-tooltip-id={tooltipId}
                          onClick={(e) => handleFeatureClick(e, feature)}
                        >
                          <img src={`${STATIC_PATH}${feature.icon}`} alt="" />
                          <Typography
                            content={feature.label}
                            font={font.medium_12}
                            color={vmTheme[theme].shortsSubText}
                            enableTrim={false}
                            align="center"
                            cursor={feature.disabled ? "default" : "pointer"}
                          />
                        </ViralFeatureCard>
                      </ViralFeatureCardWrap>
                    </CustomTooltip>
                  );
                })}
              </ViralFeatureGrid>
            </ViralSection>
          </div> : null}

          {shortData.get("socialMedia") ? (
            <SocialMedia isBorder={true} style={{ marginTop: '16px' }}>
              <Typography
                content={content.SUITABLE_FOR}
                width={"115px"}
                display={"block"}
                color={vmTheme[theme].sbSecondaryColor}
              />
              <FlexDiv justifyContent="flex-end" gap="10px">
                {shortData.get("socialMedia").map((icon) => {
                  return (
                    <Links key={icon}>
                      <img
                        src={`${STATIC_PATH + icon.split(" ")[0].toLowerCase()}.svg`}
                        alt={icon}
                      />
                    </Links>
                  );
                })}
              </FlexDiv>
            </SocialMedia>
          ) : null}
          {shortData.get("confidenceScore") ?
            <div>
              <Typography
                content={content.TRANSCRIPT}
                font={font.normalMini_14_700}
                color={vmTheme[theme].subtitleTextColor}
                padding="16px 0px"
                display="block"
                enableTrim={false}
              />
              {renderBody()}
            </div> : <div />}
        </ContentBody>
        <ReframeProgressModal
          progress={reframeProgress}
          status="processing"
          message="Generating AI Reframe..."
          show={showProgressModal}
          onClose={handleCancelProgress}
        />
      </ContentContainer>
    </>
  );
};

ShortsContent.propTypes = {
  shortData: PropTypes.object,
  inModal: PropTypes.bool,
  showPricingModal: PropTypes.func,
  feedbackType: PropTypes.oneOf(["like", "dislike", null]),
  onLike: PropTypes.func,
  onDislike: PropTypes.func,
  parentDispatch: PropTypes.func,
};

export default React.memo(ShortsContent, (prevProps, nextProps) => {
  return (
    prevProps.shortData === nextProps.shortData
    && prevProps.feedbackType === nextProps.feedbackType
  );
});
